// Deliberately not optimistic: a row moves only on the socket frame that follows a
// real change, so nothing here writes into the stream store. One in-flight write per
// hook guards against a double-tapped confirm.
import type { Decision } from '@alphadesk/ontology/objects/review.ts';
import {
  API_PATHS,
  actionReplySchema,
  type TicketRequest,
  type WebAction,
  type WebhookMint,
  type WebhookMintRequest,
  type WireTicket,
  webhookMintSchema,
  wireTicketSchema,
} from '@alphadesk/web/contract.ts';
import { useCallback, useRef, useState } from 'react';
import { useToast } from '../components/Toast.tsx';
import { note } from './posthog.ts';
import { type Parser, SIGNED_OUT, send } from './reads.ts';

// Violations are the ontology's own words; never reword them.
export interface Refused {
  ok: false;
  violations: string[];
}

export type Outcome = { ok: true; said: string; warn: boolean; value?: unknown } | Refused;

const BROKEN: Refused = {
  ok: false,
  violations: ['The bot could not be reached. Check the connection and try again.'],
};

export async function ask<T>(
  path: string,
  request: unknown,
  accepted: Parser<T>,
  method: 'POST' | 'DELETE' = 'POST',
): Promise<{ ok: true; data: T } | Refused> {
  let response: Response;
  try {
    response = await send(path, { method, body: request });
  } catch (error) {
    return error === SIGNED_OUT ? { ok: false, violations: [SIGNED_OUT.message] } : BROKEN;
  }
  const body: unknown = await response.json().catch(() => null);
  if (response.ok) {
    const parsed = accepted.safeParse(body);
    return parsed.success ? { ok: true, data: parsed.data } : BROKEN;
  }
  const parsed = actionReplySchema.safeParse(body);
  return parsed.success && parsed.data.ok === false ? parsed.data : BROKEN;
}

export async function act(
  path: string,
  request: unknown,
  method: 'POST' | 'DELETE' = 'POST',
): Promise<Outcome> {
  const answer = await ask(path, request, actionReplySchema, method);
  return answer.ok ? answer.data : answer;
}

export async function priceTicket(
  request: TicketRequest,
): Promise<{ ok: true; ticket: WireTicket } | Refused> {
  const answer = await ask(API_PATHS.ticket, request, wireTicketSchema);
  return answer.ok ? { ok: true, ticket: answer.data } : answer;
}

/** A write whose answer hands back more than words: the value it carries, parsed. */
export async function actFor<T>(
  action: WebAction,
  accepted: Parser<T>,
): Promise<{ ok: true; said: string; value: T } | Refused> {
  const outcome = await act(API_PATHS.act, action);
  if (!outcome.ok) return outcome;
  const parsed = accepted.safeParse(outcome.value);
  return parsed.success ? { ok: true, said: outcome.said, value: parsed.data } : BROKEN;
}

// The URL is a credential shown once: the desk stores only a digest.
export async function mintWebhook(
  request: WebhookMintRequest,
): Promise<{ ok: true; mint: WebhookMint } | Refused> {
  const answer = await actFor({ do: 'mint-webhook-token', ...request }, webhookMintSchema);
  return answer.ok ? { ok: true, mint: answer.value } : answer;
}

export function useWrite(): {
  run: <R extends { ok: boolean }>(write: () => Promise<R>) => Promise<R | Refused>;
  busy: boolean;
} {
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);

  const run = useCallback(
    async <R extends { ok: boolean }>(write: () => Promise<R>): Promise<R | Refused> => {
      if (inFlight.current) return { ok: false, violations: ['Already working on that one.'] };
      inFlight.current = true;
      setBusy(true);
      try {
        return await write();
      } finally {
        inFlight.current = false;
        setBusy(false);
      }
    },
    [],
  );

  return { run, busy };
}

/** `useWrite`, with a refusal said in a toast; what a success says is the caller's. */
export function useToastedWrite(): ReturnType<typeof useWrite> {
  const { run, busy } = useWrite();
  const toast = useToast();

  const toasted = useCallback(
    async <R extends { ok: boolean }>(write: () => Promise<R>): Promise<R | Refused> => {
      const answer = await run(write);
      if (isRefused(answer)) toast.refused(answer.violations);
      return answer;
    },
    [run, toast],
  );

  return { run: toasted, busy };
}

const isRefused = (answer: { ok: boolean }): answer is Refused => !answer.ok;

export function useCommit(): {
  commit: (action: WebAction, after?: { onOk?: () => void }) => Promise<Outcome>;
  busy: boolean;
} {
  const { run, busy } = useWrite();
  const toast = useToast();

  const commit = useCallback(
    async (action: WebAction, after?: { onOk?: () => void }): Promise<Outcome> => {
      const began = Date.now();
      const result = await run(() => act(API_PATHS.act, action));
      // Analytics get the violation count only, never the text (it contains figures).
      const ms = Date.now() - began;
      if (result.ok) {
        note('action taken', { do: action.do, warn: result.warn, ms });
        toast.done(result.said, result.warn);
        after?.onOk?.();
      } else {
        note('action refused', { do: action.do, violations: result.violations.length, ms });
        toast.refused(result.violations);
      }
      return result;
    },
    [run, toast],
  );

  return { commit, busy };
}

export function useDecide(ask: (decision: Decision) => WebAction, onOk: () => void) {
  const { commit, busy } = useCommit();
  return { busy, decide: (decision: Decision) => void commit(ask(decision), { onOk }) };
}
